import { css, keyframes } from 'styled-components'

const BANNER_MIN_HEIGHT = '56px'

/**
 * The message and its buttons. A zero basis lets it fill the free space while
 * the controls beside it wrap only once its text can't wrap any narrower.
 */
export const growingBannerTextRowStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  flex: 1 1 0;

  /* The message wraps onto more lines before its buttons move below it. */
  & > :first-child {
    flex: 1 1 0;
    max-width: max-content;
  }
`

interface GrowingBannerOptions {
  /** Vertical padding while shown. A single line still renders at 56px. */
  paddingBlock?: string
}

/**
 * Sizes a top banner: 56px on one line, taller when its content wraps at high
 * zoom, in a narrow window or with a long translation. Hidden banners collapse
 * to 0.
 */
export const growingBannerStyles = ({
  paddingBlock = '4px',
}: GrowingBannerOptions = {}) => {
  // `height: auto` can't transition, so the show animation clamps the banner
  // between 0 and 56px instead. Padding is part of the height, so it grows
  // from 0 too. Taller (wrapped) banners take their full height when it ends.
  const slideIn = keyframes`
    from {
      min-height: 0;
      max-height: 0;
      padding-block: 0;
    }
    to {
      min-height: ${BANNER_MIN_HEIGHT};
      max-height: ${BANNER_MIN_HEIGHT};
      padding-block: ${paddingBlock};
    }
  `

  return ({ $show }: { $show: boolean }) => css`
    min-height: ${$show ? BANNER_MIN_HEIGHT : '0'};
    height: ${$show ? 'auto' : '0'};
    padding-block: ${$show ? paddingBlock : '0'};
    overflow: hidden;
    ${
      $show &&
      css`
        animation: ${slideIn} 200ms ease;
      `
    }
  `
}
